import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, Pencil } from 'lucide-react';

const LEVEL_CFG = [
  { label: 'ADM', bg: '#1565c0', light: '#e3f2fd', text: '#1565c0' },
  { label: 'CP1', bg: '#2e7d32', light: '#e8f5e9', text: '#2e7d32' },
  { label: 'CP2', bg: '#e65100', light: '#fff3e0', text: '#e65100' },
  { label: 'CP3', bg: '#6a1b9a', light: '#f3e5f5', text: '#6a1b9a' },
  { label: 'CP4', bg: '#00695c', light: '#e0f2f1', text: '#00695c' },
  { label: 'CP5', bg: '#ad1457', light: '#fce4ec', text: '#ad1457' },
  { label: 'CP6', bg: '#4527a0', light: '#ede7f6', text: '#4527a0' },
  { label: 'CP7', bg: '#37474f', light: '#eceff1', text: '#37474f' },
];

// Used when the parent is a Team Group (e.g. GREEN YUVA):
// first level = actual DMs, second = ADMs, third+ = CPs
const TEAM_LEVEL_CFG = [
  { label: 'DM',  bg: '#1a3c2e', light: '#e8f5e9', text: '#1a3c2e' },
  { label: 'ADM', bg: '#1565c0', light: '#e3f2fd', text: '#1565c0' },
  { label: 'CP1', bg: '#2e7d32', light: '#dcfce7', text: '#2e7d32' },
  { label: 'CP2', bg: '#e65100', light: '#fff3e0', text: '#e65100' },
  { label: 'CP3', bg: '#6a1b9a', light: '#f3e5f5', text: '#6a1b9a' },
  { label: 'CP4', bg: '#00695c', light: '#e0f2f1', text: '#00695c' },
  { label: 'CP5', bg: '#ad1457', light: '#fce4ec', text: '#ad1457' },
  { label: 'CP6', bg: '#4527a0', light: '#ede7f6', text: '#4527a0' },
];

function countDescendants(node) {
  if (!node.children || node.children.length === 0) return 0;
  return node.children.reduce(function(sum, c) { return sum + 1 + countDescendants(c); }, 0);
}

export function CPTreeNode(props) {
  const node = props.node;
  const depth = props.depth || 0;
  const forceOpen = props.forceOpen;
  const canEdit = props.canEdit || false;
  const onAddChild = props.onAddChild;
  const onRemove = props.onRemove;
  const onEdit = props.onEdit;
  const dmName = props.dmName || '';
  const isTeamGroup = props.isTeamGroup || false;  // true when parent is a Team Group (e.g. GREEN YUVA)

  const [open, setOpen] = useState(depth < 1);
  const [hovered, setHovered] = useState(false);

  const cfgArr = isTeamGroup ? TEAM_LEVEL_CFG : LEVEL_CFG;
  const cfg = cfgArr[Math.min(depth, cfgArr.length - 1)];
  const hasChildren = node.children && node.children.length > 0;
  const descCount = countDescendants(node);
  const isOpen = forceOpen === 1 ? true : forceOpen === 0 ? false : open;

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1.5 pr-2 rounded-lg transition-colors"
        style={{ paddingLeft: (12 + depth * 20) + 'px', background: hovered && canEdit ? '#f8f8f7' : 'transparent' }}
        onMouseEnter={function() { setHovered(true); }}
        onMouseLeave={function() { setHovered(false); }}
      >
        {/* Toggle */}
        <div
          className="w-4 h-4 flex-shrink-0 flex items-center justify-center cursor-pointer"
          onClick={function() { if (hasChildren) setOpen(function(o) { return !o; }); }}
        >
          {hasChildren ? (
            isOpen ? React.createElement(ChevronDown, { className: 'w-3.5 h-3.5 text-stone-400' }) : React.createElement(ChevronRight, { className: 'w-3.5 h-3.5 text-stone-400' })
          ) : (
            <div className="w-1.5 h-1.5 rounded-full bg-stone-300" />
          )}
        </div>

        {/* Level badge */}
        <span
          className="text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 cursor-pointer"
          style={{ background: cfg.light, color: cfg.text }}
          onClick={function() { if (hasChildren) setOpen(function(o) { return !o; }); }}
        >
          {cfg.label}
        </span>

        {/* Name */}
        <span
          className="text-sm font-medium text-stone-700 flex-1 truncate cursor-pointer"
          onClick={function() { if (hasChildren) setOpen(function(o) { return !o; }); }}
        >
          {node.name}
        </span>

        {/* Child count */}
        {hasChildren && !canEdit && (
          <span className="text-xs text-stone-400 flex-shrink-0">
            {node.children.length}{descCount > node.children.length ? ' · ' + descCount + ' total' : ''}
          </span>
        )}

        {/* Edit buttons (only in edit mode, on hover) */}
        {canEdit && (
          <div className="flex items-center gap-1 flex-shrink-0" style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>
            {hasChildren && (
              <span className="text-xs text-stone-400 mr-1">
                {node.children.length}{descCount > node.children.length ? '·' + descCount : ''}
              </span>
            )}
            {onAddChild && (
              <button
                title={'Add CP under ' + node.name}
                onClick={function(e) { e.stopPropagation(); onAddChild(node.name, dmName); }}
                className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-emerald-100 text-emerald-600 transition-colors"
                data-testid={'add-child-' + node.name}
              >
                {React.createElement(Plus, { className: 'w-3.5 h-3.5' })}
              </button>
            )}
            {onEdit && (
              <button
                title={'Rename ' + node.name}
                onClick={function(e) { e.stopPropagation(); onEdit(node.name, dmName, cfg.label); }}
                className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-blue-100 text-blue-600 transition-colors"
                data-testid={'edit-' + node.name}
              >
                {React.createElement(Pencil, { className: 'w-3.5 h-3.5' })}
              </button>
            )}
            {onRemove && (
              <button
                title={'Remove ' + node.name}
                onClick={function(e) { e.stopPropagation(); onRemove(node.name, dmName, cfg.label); }}
                className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-red-100 text-red-500 transition-colors"
                data-testid={'remove-' + node.name}
              >
                {React.createElement(Trash2, { className: 'w-3.5 h-3.5' })}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Children */}
      {isOpen && hasChildren && (
        <div className="border-l-2 ml-6" style={{ borderColor: cfg.light }}>
          {node.children.map(function(child, i) {
            return React.createElement(CPTreeNode, {
              key: i,
              node: child,
              depth: depth + 1,
              forceOpen: forceOpen,
              canEdit: canEdit,
              onAddChild: onAddChild,
              onRemove: onRemove,
              onEdit: onEdit,
              dmName: dmName,
              isTeamGroup: isTeamGroup,
            });
          })}
        </div>
      )}
    </div>
  );
}

export default CPTreeNode;
